import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Mic2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HARI_JUMAT_LABELS = ['Ahad', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const getUpcomingFriday = () => {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 5 ? 0 : (5 - day + 7) % 7;
  const fri = new Date(today);
  fri.setDate(today.getDate() + diff);
  return fri.toISOString().split('T')[0];
};

const ImamKhatibManager = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tanggal: '', imam: '', khatib: '', tema: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/imam-khatib`, {
        withCredentials: true,
        params: { mosque_id: user?.mosque_id }
      });
      setList(data);
    } catch {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ tanggal: getUpcomingFriday(), imam: '', khatib: '', tema: '' });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ tanggal: item.tanggal, imam: item.imam, khatib: item.khatib, tema: item.tema || '' });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tanggal || !form.imam.trim() || !form.khatib.trim()) {
      return toast.error('Tanggal, Imam, dan Khatib wajib diisi');
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/imam-khatib/${editing.id}`, form, { withCredentials: true });
        toast.success('Jadwal diperbarui');
      } else {
        await axios.post(`${API_URL}/api/imam-khatib`, form, { withCredentials: true });
        toast.success('Jadwal ditambahkan');
      }
      closeModal();
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus jadwal ini?')) return;
    try {
      await axios.delete(`${API_URL}/api/imam-khatib/${id}`, { withCredentials: true });
      toast.success('Jadwal dihapus');
      fetchList();
    } catch {
      toast.error('Gagal menghapus');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = list.filter(i => i.tanggal >= today);
  const past = list.filter(i => i.tanggal < today);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" />
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-primary-dark">Jadwal Imam & Khatib</h2>
          <p className="text-sm font-body text-text-secondary mt-1">
            Input jadwal Imam dan Khatib Sholat Jumat
          </p>
        </div>
        <button
          data-testid="add-imam-khatib-btn"
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
        >
          <Plus className="w-5 h-5" /> Tambah Jadwal
        </button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Mic2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-text-secondary font-body mb-4">Belum ada jadwal imam & khatib</p>
          <button onClick={openAdd} className="text-emerald-700 font-body font-medium hover:underline">
            Tambah jadwal pertama
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div>
              <h3 className="text-sm font-body font-semibold text-stone-500 uppercase tracking-wider mb-3">Mendatang</h3>
              <div className="space-y-3">
                {upcoming.map((item) => (
                  <JadwalCard key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} isUpcoming />
                ))}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <h3 className="text-sm font-body font-semibold text-stone-500 uppercase tracking-wider mb-3">Riwayat</h3>
              <div className="space-y-3">
                {past.slice(0, 10).map((item) => (
                  <JadwalCard key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-xl w-full max-w-lg overflow-hidden"
            >
              <div className="border-b border-stone-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-heading font-bold text-primary-dark">
                  {editing ? 'Edit Jadwal' : 'Tambah Jadwal Jumat'}
                </h3>
                <button onClick={closeModal} className="p-2 hover:bg-stone-100 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Tanggal Jumat *
                  </label>
                  <input
                    type="date"
                    data-testid="imam-khatib-tanggal-input"
                    value={form.tanggal}
                    onChange={e => setForm({ ...form, tanggal: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Imam Sholat Jumat *
                  </label>
                  <input
                    type="text"
                    data-testid="imam-input"
                    value={form.imam}
                    onChange={e => setForm({ ...form, imam: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: Ustadz H. Ahmad Fauzi, Lc."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Khatib Sholat Jumat *
                  </label>
                  <input
                    type="text"
                    data-testid="khatib-input"
                    value={form.khatib}
                    onChange={e => setForm({ ...form, khatib: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: KH. Muhammad Rizki, M.Ag"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Tema Khutbah <span className="text-xs text-stone-400">(opsional)</span>
                  </label>
                  <input
                    type="text"
                    data-testid="tema-input"
                    value={form.tema}
                    onChange={e => setForm({ ...form, tema: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: Mempererat Ukhuwah Islamiyah"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={closeModal}
                    className="flex-1 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-body font-medium transition-colors">
                    Batal
                  </button>
                  <button type="submit" disabled={saving} data-testid="imam-khatib-submit-btn"
                    className="flex-1 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors disabled:opacity-50">
                    {saving ? 'Menyimpan...' : editing ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const JadwalCard = ({ item, onEdit, onDelete, isUpcoming }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-white rounded-2xl border p-4 flex items-start gap-4 ${isUpcoming ? 'border-emerald-200 shadow-sm' : 'border-stone-100'}`}
    data-testid={`imam-khatib-item-${item.id}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isUpcoming ? 'bg-emerald-700' : 'bg-stone-100'}`}>
      <Mic2 className={`w-5 h-5 ${isUpcoming ? 'text-white' : 'text-stone-400'}`} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="w-3.5 h-3.5 text-stone-400" />
        <span className={`text-xs font-medium ${isUpcoming ? 'text-emerald-700' : 'text-stone-500'}`}>
          {formatDate(item.tanggal)}
        </span>
        {isUpcoming && <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Mendatang</span>}
      </div>
      <div className="space-y-0.5">
        <p className="text-sm font-body font-semibold text-text-primary">
          Imam: <span className="font-normal text-stone-700">{item.imam}</span>
        </p>
        <p className="text-sm font-body font-semibold text-text-primary">
          Khatib: <span className="font-normal text-stone-700">{item.khatib}</span>
        </p>
        {item.tema && (
          <p className="text-xs text-stone-500 italic mt-1">"{item.tema}"</p>
        )}
      </div>
    </div>
    <div className="flex gap-2 flex-shrink-0">
      <button onClick={() => onEdit(item)}
        className="w-8 h-8 rounded-lg bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
        data-testid={`edit-imam-khatib-${item.id}`}>
        <Edit2 className="w-3.5 h-3.5 text-stone-600" />
      </button>
      <button onClick={() => onDelete(item.id)}
        className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
        data-testid={`delete-imam-khatib-${item.id}`}>
        <Trash2 className="w-3.5 h-3.5 text-red-500" />
      </button>
    </div>
  </motion.div>
);

export default ImamKhatibManager;
